import React from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { useVirtualList } from './useVirtualList';
import type { UseVirtualListParams } from './useVirtualList';

export interface VirtualListProps<T> extends UseVirtualListParams<T> {
    /** Called only for the rows in view (+ overscan). */
    renderItem: (item: T, index: number) => ReactNode;
}

/**
 * Vertical list that mounts only the rows in view; a scroll-range change re-renders this list, not its parent.
 *
 * Constraints for rows:
 * - space rows only through `gap`; no vertical margin on a row's root — it lands in the measured slot height on top
 *   of the gap and drifts from `estimateSize`;
 * - the list clips horizontally (`overflow-x: clip`), so anything a row draws outside its width is cut —
 *   render popovers through a portal.
 *
 * See `useVirtualList` for `scrollElement` / `scroll-behavior` requirements and the content-above-the-list rule.
 */
export function VirtualList<T>({ renderItem, ...params }: VirtualListProps<T>) {
    const { listRef, virtualItems, totalSize, scrollMargin, measureElement } = useVirtualList(params);

    return (
        // Height/transform go inline: as styled props they would mint a class per value.
        <Root ref={listRef} style={{ height: totalSize }}>
            {virtualItems.map((v) => (
                <RowSlot
                    key={v.key}
                    data-index={v.index}
                    ref={measureElement}
                    style={{ transform: `translateY(${v.start - scrollMargin}px)` }}
                >
                    {renderItem(params.items[v.index], v.index)}
                </RowSlot>
            ))}
        </Root>
    );
}

export default VirtualList;

const Root = styled.div`
    position: relative;
    width: 100%;
    /* The virtualizer corrects offsets for rows resized above the view; browser anchoring would double it. */
    overflow-anchor: none;
    /* A row animating out sideways (e.g. a swipe's translate -110%) would overflow, which is scrollable in RTL:
       iOS swipe momentum then scrolled the whole screen sideways. clip keeps this from being a scroller. */
    overflow-x: clip;
`;

// Positioned wrapper around the row, so a row can animate its own transform.
const RowSlot = styled.div`
    position: absolute;
    top: 0;
    inset-inline: 0;
    width: 100%;
`;
